import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { ShieldCheck, CheckCircle2, Mail, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../../lib/auth/useAuth";
import { useWaitlistStatus } from "../../lib/waitlist/useWaitlistStatus";

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

export function WaitlistPending() {
  const { session, loading, user, signOut } = useAuth();
  const waitlist = useWaitlistStatus();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  // Not signed in → back to /login. Handled client-side so the page is
  // reachable outside AuthGate without giving up the session check.
  if (!E2E_BYPASS && !loading && !session) {
    return <Navigate to="/login" replace />;
  }

  const allowlisted = waitlist.status === "allowlisted";

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/login", { replace: true });
    } finally {
      setSigningOut(false);
    }
  }

  const email = user?.email ?? "";

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Inter',sans-serif] flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6 text-slate-900 font-bold text-lg">
          <ShieldCheck className="w-6 h-6 text-[#0070F2]" />
          <span>Proofly</span>
        </div>

        <div className="flex items-start gap-3 mb-4">
          <CheckCircle2 className="w-6 h-6 text-emerald-500 mt-0.5 shrink-0" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              You're on the list
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Proofly is in closed alpha. We're letting people in in small
              batches so we can hand-hold the onboarding.
            </p>
          </div>
        </div>

        {email && (
          <div
            data-testid="waitlist-email"
            className="mt-5 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
          >
            <Mail className="w-4 h-4 text-slate-400" />
            <span className="truncate">{email}</span>
          </div>
        )}

        <p className="mt-5 text-sm text-slate-600">
          We'll email you at this address when your account is ready. Nothing
          you need to do in the meantime.
        </p>

        {/* If the user is already allowlisted (stale tab, admin just flipped
            them, or they manually typed the URL), surface a direct "go to
            the app" CTA instead of silently redirecting — less magical and
            keeps the page testable. */}
        {allowlisted && (
          <Link
            to="/inbox"
            data-testid="waitlist-go-to-app"
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#0070F2] hover:text-[#005BCA]"
          >
            Your account is ready — open Proofly
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}

        <div className="mt-6 flex items-center justify-between text-sm">
          <Button
            type="button"
            variant="ghost"
            onClick={handleSignOut}
            disabled={signingOut}
            data-testid="waitlist-sign-out"
            className="text-slate-600 hover:text-slate-900"
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </Button>
          <a
            href="mailto:team@proofly.one"
            className="text-slate-500 hover:text-slate-900 underline underline-offset-2"
          >
            Contact us
          </a>
        </div>
      </div>
    </div>
  );
}
